/**
 * A stable, per-LINK storage key (SPEC_CAPTURE_UX_R2 §3.1 "persisted per link", §4 "once per type per case").
 *
 * The mute choice and the "this shot type has been demonstrated" set are per CASE, not per device and not per
 * shot: the same link re-opened in the same browser must remember both, a different link must not inherit them.
 *
 * The link token is a CREDENTIAL (client.ts: exchanged for a memory-only bearer, never persisted) — so it never
 * becomes a storage key. `clientRef` (the tenant's own reference on the exchange summary) is used when the
 * exchange has landed; before that, a 32-bit FNV-1a digest of the token stands in — stable for this link, not
 * reversible into the token.
 */

import { useCaptureStore } from '@/store/captureStore';

export function fnv1a32(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** `<clientRef>` when known, else `t<digest>` of the link token, else `anon` (nothing to key on yet). */
export function linkKey(): string {
  const state = useCaptureStore.getState();
  const ref = state.summary?.clientRef;
  if (typeof ref === 'string' && ref.trim() !== '') return ref.trim().slice(0, 64);
  const token = state.linkToken;
  if (typeof token === 'string' && token.trim() !== '') return `t${fnv1a32(token.trim())}`;
  return 'anon';
}

/** `<prefix>:<link key>` — the one place a per-link storage name is built. */
export function linkScopedKey(prefix: string): string {
  return `${prefix}:${linkKey()}`;
}
